import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  ArrowLeft, Save, Presentation, Send, Eye, EyeOff, GripVertical,
  Plus, Trash2, Upload, FileText, Calculator, PenTool, FileCheck,
  Image, Video, Home, Lock, Unlock, Loader2, Check, X, 
  ChevronRight, Settings, Camera, Shield, DollarSign, Percent
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// PAGE TYPE ICONS & LABELS
// ============================================================================

const PAGE_TYPE_CONFIG = {
  COVER: { label: 'Cover', icon: Home, color: 'bg-purple-100 text-purple-700' },
  DETAILS: { label: 'Details', icon: Calculator, color: 'bg-blue-100 text-blue-700' },
  WARRANTY: { label: 'Warranty', icon: FileCheck, color: 'bg-emerald-100 text-emerald-700' },
  ABOUT_COMPANY: { label: 'About Us', icon: Shield, color: 'bg-indigo-100 text-indigo-700' },
  WARRANTY_PDF: { label: 'Warranty Details', icon: FileText, color: 'bg-teal-100 text-teal-700' },
  FINANCING: { label: 'Financing', icon: DollarSign, color: 'bg-violet-100 text-violet-700' },
  SIGNATURE: { label: 'Signature', icon: PenTool, color: 'bg-green-100 text-green-700' },
  TERMS: { label: 'Terms', icon: FileCheck, color: 'bg-amber-100 text-amber-700' },
  UPLOAD_PDF: { label: 'PDF', icon: FileText, color: 'bg-red-100 text-red-700' },
  UPLOAD_IMAGE: { label: 'Image', icon: Image, color: 'bg-cyan-100 text-cyan-700' },
  VIDEO: { label: 'Video', icon: Video, color: 'bg-pink-100 text-pink-700' },
  CUSTOM_TEXT: { label: 'Text', icon: FileText, color: 'bg-slate-100 text-slate-700' },
};

// Unit options for line items (SumoQuote-style)
const UNIT_OPTIONS = [
  { value: 'EA', label: 'Each' },
  { value: 'SQFT', label: 'Sq Ft' },
  { value: 'SQ', label: 'Square (100 sqft)' },
  { value: 'LF', label: 'Linear Ft' },
  { value: 'GAL', label: 'Gallon' },
  { value: 'HR', label: 'Hour' },
  { value: 'PC', label: 'Piece' },
  { value: 'BDL', label: 'Bundle' },
];

// ============================================================================
// DEFAULT TERMS TEXT
// ============================================================================

const DEFAULT_TERMS_TEXT = `TERMS AND CONDITIONS

1. SCOPE OF WORK
This estimate covers the work described in the preceding pages. Any additional work requested will require a separate estimate and approval.

2. PAYMENT TERMS
- 50% deposit required to schedule work
- Balance due upon completion
- Accepted payment methods: Check, Credit Card, ACH

3. WARRANTY
Rhino Shield of Florida provides the following warranty coverage:
- 25-Year Transferable Warranty on coating materials
- 5-Year Warranty on workmanship
- Warranty is void if surfaces are altered or damaged after application

4. SCHEDULING
- Work will be scheduled upon receipt of signed estimate and deposit
- Estimated start date will be provided within 3 business days of deposit
- Weather delays may affect schedule

5. CUSTOMER RESPONSIBILITIES
- Ensure access to work areas
- Remove or protect personal items in work areas
- Notify us of any known issues with surfaces

6. ACCEPTANCE
By signing this estimate, the customer agrees to the terms and conditions stated above and authorizes the work to proceed.`;

// ============================================================================
// PAGE LIST ITEM (Sortable)
// ============================================================================

const PageListItem = ({ page, index, isSelected, onSelect, onToggleVisibility, isTerms }) => {
  const config = PAGE_TYPE_CONFIG[page.page_type] || PAGE_TYPE_CONFIG.CUSTOM_TEXT;
  const PageIcon = config.icon;

  return (
    <Draggable draggableId={page.id} index={index} isDragDisabled={isTerms}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
            isSelected 
              ? 'bg-blue-100 dark:bg-blue-900/50 border-2 border-blue-400 dark:border-blue-500' 
              : 'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
          } ${snapshot.isDragging ? 'shadow-lg' : ''}`}
          onClick={() => onSelect(page)}
          data-testid={`page-item-${page.id}`}
        >
          {/* Drag Handle */}
          <div 
            {...provided.dragHandleProps} 
            className={`text-slate-400 dark:text-slate-500 ${isTerms ? 'opacity-30 cursor-not-allowed' : 'hover:text-slate-600 dark:hover:text-slate-300 cursor-grab'}`}
          >
            <GripVertical className="h-4 w-4" />
          </div>
          
          {/* Page Icon */}
          <div className={`p-1.5 rounded ${config.color}`}>
            <PageIcon className="h-3.5 w-3.5" />
          </div>
          
          {/* Page Title */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate dark:text-white">
              {page.title || config.label}
            </p>
          </div>
          
          {/* Visibility Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!isTerms) onToggleVisibility(page);
            }}
            className={`p-1 rounded ${isTerms ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-100 dark:hover:bg-slate-600'}`}
            title={page.customer_visible ? 'Visible to customer' : 'Hidden from customer'}
            disabled={isTerms}
          >
            {page.customer_visible ? (
              <Eye className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : (
              <EyeOff className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            )}
          </button>
          
          {/* Lock indicator for Terms */}
          {isTerms && (
            <Lock className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" title="Terms page is locked to the end" />
          )}
        </div>
      )}
    </Draggable>
  );
};

// ============================================================================
// COVER PAGE EDITOR
// ============================================================================

const CoverPageEditor = ({ page, job, jobPhotos, onUpdate }) => {
  const initialConfig = page.config || {};
  const [config, setConfig] = useState(initialConfig);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sync with page prop changes
  const pageId = page.id;
  useEffect(() => {
    setConfig(page.config || {});
  }, [pageId, page.config]);

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    onUpdate({ config: newConfig });
  };

  const handlePhotoSelect = (photoUrl) => {
    handleConfigChange('cover_image_url', photoUrl);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`${API}/upload`, formData);
      // Construct full URL from the returned path
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      handleConfigChange('cover_image_url', fullUrl);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Label>Cover Title</Label>
        <Input
          value={config.estimate_title || ''}
          onChange={(e) => handleConfigChange('estimate_title', e.target.value)}
          placeholder="Estimate Title"
          className="mt-1"
        />
      </div>

      <div>
        <Label>Customer Name</Label>
        <Input
          value={config.customer_name || job?.customer_name || ''}
          onChange={(e) => handleConfigChange('customer_name', e.target.value)}
          placeholder="Customer Name"
          className="mt-1"
        />
      </div>

      <div>
        <Label>Address</Label>
        <Input
          value={config.address || job?.address || ''}
          onChange={(e) => handleConfigChange('address', e.target.value)}
          placeholder="Project Address"
          className="mt-1"
        />
      </div>

      <div>
        <Label>Prepared By</Label>
        <Input
          value={config.prepared_by || ''}
          onChange={(e) => handleConfigChange('prepared_by', e.target.value)}
          placeholder="RhinoShield Florida"
          className="mt-1"
        />
      </div>

      <div>
        <Label className="mb-2 block">Cover Image</Label>
        
        {/* Current Image Preview */}
        {config.cover_image_url && (
          <div className="mb-3 relative">
            <img 
              src={config.cover_image_url} 
              alt="Cover" 
              className="w-full h-40 object-cover rounded-lg border"
            />
            <Button
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => handleConfigChange('cover_image_url', null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Upload or Select */}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload Image
          </Button>
        </div>

        {/* Job Photos Selection */}
        {jobPhotos && jobPhotos.length > 0 && (
          <div className="mt-4">
            <Label className="text-sm text-slate-500">Or select from Job Photos:</Label>
            <div className="grid grid-cols-4 gap-2 mt-2 max-h-32 overflow-y-auto">
              {jobPhotos.map((photo, i) => (
                <button
                  key={i}
                  onClick={() => handlePhotoSelect(photo.url)}
                  className={`aspect-square rounded border-2 overflow-hidden ${
                    config.cover_image_url === photo.url 
                      ? 'border-blue-500' 
                      : 'border-transparent hover:border-slate-300'
                  }`}
                >
                  <img src={photo.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// DETAILS PAGE EDITOR (Enhanced with Financial Control)
// ============================================================================

const DetailsPageEditor = ({ page, estimate, measurements, lineItems, onUpdate, onLineItemsChange, onEstimateChange, onPricingModeChange }) => {
  const config = page.config || {};
  const globalMargin = estimate?.global_margin_percent || 30;
  const [showFinancialControl, setShowFinancialControl] = useState(false);
  const [showPriceListPicker, setShowPriceListPicker] = useState(false);
  const [priceListItems, setPriceListItems] = useState([]);
  const [loadingPriceList, setLoadingPriceList] = useState(false);
  
  // Pricing mode: 'today' ($9/sqft) or '90day' ($15/sqft)
  const pricingMode = estimate?.pricing_mode || 'today';
  const todayPricePerSqft = 9.00;
  const ninetyDayPricePerSqft = 15.00;
  
  // Use override values if set, otherwise fall back to job measurements
  const coatingSqftValue = estimate?.coating_sqft_override ?? measurements?.coating_sqft ?? 0;
  const repairSqftValue = estimate?.repair_sqft_override ?? measurements?.repair_sqft ?? 0;
  const crewLaborPercent = estimate?.crew_labor_percent ?? 0;
  const commissionPercent = estimate?.commission_percent ?? 0;
  
  // Local state for input fields to allow empty values during editing
  const [coatingSqftInput, setCoatingSqftInput] = useState(() => coatingSqftValue === 0 ? '' : String(coatingSqftValue));
  const [repairSqftInput, setRepairSqftInput] = useState(() => repairSqftValue === 0 ? '' : String(repairSqftValue));
  const [crewLaborInput, setCrewLaborInput] = useState(() => crewLaborPercent === 0 ? '' : String(crewLaborPercent));
  const [commissionInput, setCommissionInput] = useState(() => commissionPercent === 0 ? '' : String(commissionPercent));
  
  // Use numeric values for calculations
  const coatingSqft = coatingSqftInput === '' ? 0 : parseFloat(coatingSqftInput) || 0;
  const repairSqft = repairSqftInput === '' ? 0 : parseFloat(repairSqftInput) || 0;

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    onUpdate({ config: newConfig });
  };

  // Calculate totals based on pricing mode
  const totalSqft = coatingSqft + repairSqft;
  const basePrice = pricingMode === 'today' 
    ? totalSqft * todayPricePerSqft 
    : totalSqft * ninetyDayPricePerSqft;
  
  // Line item totals (additional to base price)
  const lineItemsTotal = lineItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const materialCostTotal = lineItems.reduce((sum, item) => 
    sum + (item.material_cost || 0) * (item.quantity || 1), 0);
  const laborCostTotal = lineItems.reduce((sum, item) => 
    sum + (item.labor_cost || 0) * (item.quantity || 1), 0);
  
  // Final totals
  const subtotal = basePrice + lineItemsTotal;
  const estimatedProfit = subtotal - materialCostTotal - laborCostTotal;
  
  // Financial calculations (internal) - using local state values for real-time calculation
  const crewLaborPercentValue = crewLaborInput === '' ? 0 : parseFloat(crewLaborInput) || 0;
  const commissionPercentValue = commissionInput === '' ? 0 : parseFloat(commissionInput) || 0;
  const crewLaborCost = (subtotal * crewLaborPercentValue) / 100;
  const commissionCost = (subtotal * commissionPercentValue) / 100;
  const netProfit = estimatedProfit - crewLaborCost - commissionCost;

  // Line item handlers
  const addLineItem = () => {
    const newItem = {
      id: `temp-${Date.now()}`,
      category: 'MATERIAL',
      name: '',
      quantity: 1,
      unit: 'SQFT',
      material_cost: 0,
      labor_cost: 0,
      margin_percent: globalMargin,
      unit_price: 0,
      total_price: 0,
      visible_to_customer: true,
      show_unit_price: true,
      show_quantity: true
    };
    onLineItemsChange([...lineItems, newItem]);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Recalculate price when cost/margin changes
    if (['material_cost', 'labor_cost', 'margin_percent', 'quantity'].includes(field)) {
      const item = updated[index];
      const baseCost = (item.material_cost || 0) + (item.labor_cost || 0);
      const marginAmount = baseCost * ((item.margin_percent || 0) / 100);
      const unitPrice = baseCost + marginAmount;
      updated[index].unit_price = Math.round(unitPrice * 100) / 100;
      updated[index].total_price = Math.round(unitPrice * (item.quantity || 1) * 100) / 100;
    }
    
    onLineItemsChange(updated);
  };

  const removeLineItem = (index) => {
    onLineItemsChange(lineItems.filter((_, i) => i !== index));
  };

  // Apply global margin to all items
  const applyGlobalMargin = () => {
    const updated = lineItems.map(item => {
      const baseCost = (item.material_cost || 0) + (item.labor_cost || 0);
      const marginAmount = baseCost * (globalMargin / 100);
      const unitPrice = baseCost + marginAmount;
      return {
        ...item,
        margin_percent: globalMargin,
        unit_price: Math.round(unitPrice * 100) / 100,
        total_price: Math.round(unitPrice * (item.quantity || 1) * 100) / 100
      };
    });
    onLineItemsChange(updated);
    toast.success(`Applied ${globalMargin}% margin to all items`);
  };

  // Handle measurement override changes
  const handleCoatingSqftChange = (inputValue) => {
    setCoatingSqftInput(inputValue);
    const numValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
    onEstimateChange({ coating_sqft_override: numValue });
  };

  const handleRepairSqftChange = (inputValue) => {
    setRepairSqftInput(inputValue);
    const numValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
    onEstimateChange({ repair_sqft_override: numValue });
  };

  // Handle financial control changes
  const handleMarginChange = (value) => {
    onEstimateChange({ global_margin_percent: value });
  };

  const handleCrewLaborChange = (inputValue) => {
    setCrewLaborInput(inputValue);
    const numValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
    onEstimateChange({ crew_labor_percent: numValue });
  };

  const handleCommissionChange = (inputValue) => {
    setCommissionInput(inputValue);
    const numValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
    onEstimateChange({ commission_percent: numValue });
  };

  // Fetch price list items
  const fetchPriceList = async () => {
    setLoadingPriceList(true);
    try {
      const response = await axios.get(`${API}/price-list`);
      setPriceListItems(response.data.filter(item => item.is_active));
    } catch (error) {
      console.error('Error fetching price list:', error);
      toast.error('Failed to load price list');
    } finally {
      setLoadingPriceList(false);
    }
  };

  // Add item from price list
  const addFromPriceList = (priceItem) => {
    const newItem = {
      id: `temp-${Date.now()}`,
      price_list_item_id: priceItem.id,
      category: priceItem.category,
      name: priceItem.name,
      description: priceItem.description,
      quantity: 1,
      unit: priceItem.unit,
      material_cost: priceItem.material_cost,
      labor_cost: priceItem.labor_cost,
      margin_percent: priceItem.default_margin_percent || globalMargin,
      unit_price: priceItem.default_unit_price,
      total_price: priceItem.default_unit_price,
      visible_to_customer: true,
      show_unit_price: true,
      show_quantity: true
    };
    onLineItemsChange([...lineItems, newItem]);
    toast.success(`Added: ${priceItem.name}`);
  };

  // Open price list picker
  const openPriceListPicker = () => {
    fetchPriceList();
    setShowPriceListPicker(true);
  };

  return (
    <div className="space-y-6">
      {/* Measurements Summary - Editable */}
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm dark:text-white">Measurements</CardTitle>
          {measurements && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Job values: Coating {measurements.coating_sqft?.toLocaleString() || 0} sqft, Repair {measurements.repair_sqft?.toLocaleString() || 0} sqft
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded">
              <Label className="text-slate-500 dark:text-slate-400 text-xs mb-1 block">Coating (sqft)</Label>
              <Input
                type="number"
                value={coatingSqftInput}
                onChange={(e) => handleCoatingSqftChange(e.target.value)}
                className="h-8 text-sm font-medium dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                placeholder="0"
              />
              <p className="text-xs text-slate-400 mt-1">{measurements?.coating_lf?.toLocaleString() || 0} LF</p>
            </div>
            <div className="p-3 bg-amber-50 dark:bg-amber-900/30 rounded">
              <Label className="text-slate-500 dark:text-slate-400 text-xs mb-1 block">Repair (sqft)</Label>
              <Input
                type="number"
                value={repairSqftInput}
                onChange={(e) => handleRepairSqftChange(e.target.value)}
                className="h-8 text-sm font-medium dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                placeholder="0"
              />
              <p className="text-xs text-slate-400 mt-1">{measurements?.repair_lf?.toLocaleString() || 0} LF</p>
            </div>
          </div>
          
          {/* Pricing Mode Selection */}
          <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-3">Pricing Option</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="pricingMode"
                  checked={pricingMode === 'today'}
                  onChange={() => onPricingModeChange('today')}
                  className="w-4 h-4 text-green-600"
                />
                <div className="flex-1">
                  <span className="font-medium text-green-800 dark:text-green-300">Today Price</span>
                  <span className="ml-2 text-green-600 dark:text-green-400 font-bold">${todayPricePerSqft.toFixed(2)}/sqft</span>
                </div>
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  ${(totalSqft * todayPricePerSqft).toLocaleString()}
                </span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="pricingMode"
                  checked={pricingMode === '90day'}
                  onChange={() => onPricingModeChange('90day')}
                  className="w-4 h-4 text-amber-600"
                />
                <div className="flex-1">
                  <span className="font-medium text-amber-800 dark:text-amber-300">90 Day Price</span>
                  <span className="ml-2 text-amber-600 dark:text-amber-400 font-bold">${ninetyDayPricePerSqft.toFixed(2)}/sqft</span>
                </div>
                <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                  ${(totalSqft * ninetyDayPricePerSqft).toLocaleString()}
                </span>
              </label>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400 mt-2">
              Total Area: {totalSqft.toLocaleString()} sqft
            </p>
          </div>
          
          <div className="flex items-center gap-2 mt-3">
            <Switch
              checked={config.show_measurements !== false}
              onCheckedChange={(v) => handleConfigChange('show_measurements', v)}
            />
            <Label className="text-sm dark:text-slate-300">Show measurements to customer</Label>
          </div>
        </CardContent>
      </Card>

      {/* Line Items (SumoQuote-style) */}
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm dark:text-white">Line Items</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={openPriceListPicker}>
                <Calculator className="h-3.5 w-3.5 mr-1" />
                From Price List
              </Button>
              <Button variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Custom Item
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {lineItems.length === 0 ? (
            <div className="text-center py-8">
              <Calculator className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400">No line items added</p>
              <div className="flex gap-2 justify-center mt-2">
                <Button variant="outline" size="sm" onClick={openPriceListPicker}>
                  <Calculator className="h-3.5 w-3.5 mr-1" />
                  From Price List
                </Button>
                <Button variant="outline" size="sm" onClick={addLineItem}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Custom Item
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Header row */}
              <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 dark:text-slate-400 font-medium px-2">
                <div className="col-span-3">Name</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-1">Unit</div>
                <div className="col-span-2">Mat. Cost</div>
                <div className="col-span-2">Labor Cost</div>
                <div className="col-span-1">Margin</div>
                <div className="col-span-1">Price</div>
                <div className="col-span-1"></div>
              </div>
              
              {lineItems.map((item, i) => (
                <div key={item.id || i} className="grid grid-cols-12 gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded items-center">
                  <div className="col-span-3">
                    <Input
                      value={item.name}
                      onChange={(e) => updateLineItem(i, 'name', e.target.value)}
                      placeholder="Item name"
                      className="h-8 text-sm dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(i, 'quantity', parseFloat(e.target.value) || 0)}
                      className="h-8 text-sm dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>
                  <div className="col-span-1">
                    <Select
                      value={item.unit}
                      onValueChange={(v) => updateLineItem(i, 'unit', v)}
                    >
                      <SelectTrigger className="h-8 text-xs dark:bg-slate-700 dark:border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                      <Input
                        type="number"
                        value={item.material_cost}
                        onChange={(e) => updateLineItem(i, 'material_cost', parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm pl-6 dark:bg-slate-700 dark:border-slate-600"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                      <Input
                        type="number"
                        value={item.labor_cost}
                        onChange={(e) => updateLineItem(i, 'labor_cost', parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm pl-6 dark:bg-slate-700 dark:border-slate-600"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="relative">
                      <Input
                        type="number"
                        value={item.margin_percent}
                        onChange={(e) => updateLineItem(i, 'margin_percent', parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm pr-5 dark:bg-slate-700 dark:border-slate-600"
                      />
                      <Percent className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                    </div>
                  </div>
                  <div className="col-span-1 text-right">
                    <span className="text-sm font-medium dark:text-white">
                      ${(item.total_price || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => updateLineItem(i, 'visible_to_customer', !item.visible_to_customer)}
                      className="p-1 h-7 w-7"
                    >
                      {item.visible_to_customer ? (
                        <Eye className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <EyeOff className="h-3.5 w-3.5 text-slate-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLineItem(i)}
                      className="p-1 h-7 w-7 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* FINANCIAL CONTROL - Collapsible (Internal Only) */}
      <Card className="border-2 border-dashed border-orange-300 dark:border-orange-600 bg-orange-50/50 dark:bg-orange-900/20">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowFinancialControl(!showFinancialControl)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-orange-600" />
              <CardTitle className="text-sm text-orange-800 dark:text-orange-400">
                Financial Control (Internal Only)
              </CardTitle>
            </div>
            <ChevronRight className={`h-4 w-4 text-orange-600 transition-transform ${showFinancialControl ? 'rotate-90' : ''}`} />
          </div>
          <p className="text-xs text-orange-600 dark:text-orange-400">
            This section is NOT visible to customers
          </p>
        </CardHeader>
        {showFinancialControl && (
          <CardContent>
            <div className="space-y-4">
              {/* Global Margin */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-sm mb-2 block dark:text-slate-300">Margin: {globalMargin}%</Label>
                  <Slider
                    value={[globalMargin]}
                    onValueChange={([value]) => handleMarginChange(value)}
                    min={0}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={applyGlobalMargin}
                  className="whitespace-nowrap"
                >
                  Apply to All
                </Button>
              </div>
              
              {/* Labor and Commission Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-2 block dark:text-slate-300">
                    Crew Labor (% of gross)
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={crewLaborInput}
                      onChange={(e) => handleCrewLaborChange(e.target.value)}
                      className="h-9 pr-8 dark:bg-slate-700 dark:border-slate-600"
                      placeholder="0"
                      min={0}
                      max={100}
                    />
                    <Percent className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Cost: ${crewLaborCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                </div>
                <div>
                  <Label className="text-sm mb-2 block dark:text-slate-300">
                    Commission (%)
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={commissionInput}
                      onChange={(e) => handleCommissionChange(e.target.value)}
                      className="h-9 pr-8 dark:bg-slate-700 dark:border-slate-600"
                      placeholder="0"
                      min={0}
                      max={100}
                    />
                    <Percent className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Cost: ${commissionCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </p>
                </div>
              </div>
              
              {/* Internal cost breakdown */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg text-sm">
                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">Material Cost</p>
                  <p className="font-medium text-red-600">${materialCostTotal.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">Labor Cost</p>
                  <p className="font-medium text-red-600">${laborCostTotal.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">Crew Labor</p>
                  <p className="font-medium text-red-600">${crewLaborCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
                <div>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">Commission</p>
                  <p className="font-medium text-red-600">${commissionCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
              </div>
              
              {/* Net Profit Summary */}
              <div className="p-3 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg border border-green-300 dark:border-green-700">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-green-700 dark:text-green-400">Gross Profit</p>
                    <p className="font-medium text-green-800 dark:text-green-300">${estimatedProfit.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-green-700 dark:text-green-400">Net Profit (after labor & commission)</p>
                    <p className="font-bold text-lg text-green-800 dark:text-green-300">${netProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pricing Summary (Customer-visible total) */}
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm dark:text-white">Customer Pricing Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            {/* Base Price from sqft */}
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">
                Base Price ({totalSqft.toLocaleString()} sqft × ${pricingMode === 'today' ? todayPricePerSqft : ninetyDayPricePerSqft}/sqft)
              </span>
              <span className="font-medium dark:text-white">
                ${basePrice.toLocaleString()}
              </span>
            </div>
            {/* Additional Line Items */}
            {lineItemsTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Additional Items</span>
                <span className="font-medium dark:text-white">
                  ${lineItemsTotal.toLocaleString()}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t dark:border-slate-600 pt-2">
              <span className="text-slate-500 dark:text-slate-400">Subtotal</span>
              <span className="font-medium dark:text-white">
                ${subtotal.toLocaleString()}
              </span>
            </div>
            {estimate?.tax_percent > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Tax ({estimate.tax_percent}%)</span>
                <span className="font-medium dark:text-white">
                  ${((subtotal * estimate.tax_percent) / 100).toLocaleString()}
                </span>
              </div>
            )}
            {estimate?.discount_percent > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount ({estimate.discount_percent}%)</span>
                <span className="font-medium">
                  -${((subtotal * estimate.discount_percent) / 100).toLocaleString()}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t dark:border-slate-600 pt-2 text-lg font-bold">
              <span className="dark:text-white">Total</span>
              <span className={`${pricingMode === 'today' ? 'text-green-600' : 'text-amber-600'}`}>
                ${subtotal.toLocaleString()}
              </span>
            </div>
            {pricingMode === 'today' && (
              <p className="text-xs text-green-600 text-center mt-2">
                ✓ Today&apos;s special pricing applied!
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Price List Picker Dialog */}
      <Dialog open={showPriceListPicker} onOpenChange={setShowPriceListPicker}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add from Price List</DialogTitle>
            <DialogDescription>
              Select items from your price list to add to this estimate
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {loadingPriceList ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : priceListItems.length === 0 ? (
              <div className="text-center py-12">
                <Calculator className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-2">No price list items found</p>
                <p className="text-sm text-slate-400">
                  Add items in Settings → Costs & Margins → Price List
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {priceListItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                    onClick={() => addFromPriceList(item)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {item.category}
                        </Badge>
                      </div>
                      {item.description && (
                        <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                      )}
                      <div className="flex gap-4 mt-1 text-xs text-slate-400">
                        <span>Material: ${item.material_cost?.toFixed(2)}</span>
                        <span>Labor: ${item.labor_cost?.toFixed(2)}</span>
                        <span>Margin: {item.default_margin_percent}%</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">${item.default_unit_price?.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">per {item.unit}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowPriceListPicker(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// WARRANTY PAGE EDITOR
// ============================================================================

const WarrantyPageEditor = ({ page, estimate, onUpdate, onWarrantiesChange }) => {
  const warranties = estimate?.warranties || [];

  const addWarranty = () => {
    const newWarranty = {
      id: `temp-${Date.now()}`,
      warranty_type: 'MATERIAL',
      name: '',
      description: '',
      term_years: 1,
      is_transferable: false,
      coverage_details: ''
    };
    onWarrantiesChange([...warranties, newWarranty]);
  };

  const updateWarranty = (index, field, value) => {
    const updated = [...warranties];
    updated[index] = { ...updated[index], [field]: value };
    onWarrantiesChange(updated);
  };

  const removeWarranty = (index) => {
    onWarrantiesChange(warranties.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-emerald-600" />
              <CardTitle className="text-sm dark:text-white">Warranty Information</CardTitle>
            </div>
            <Button variant="outline" size="sm" onClick={addWarranty}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Warranty
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {warranties.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400">No warranties added</p>
              <Button variant="outline" size="sm" onClick={addWarranty} className="mt-2">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Warranty
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {warranties.map((warranty, i) => (
                <div key={warranty.id || i} className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-start justify-between mb-3">
                    <Select
                      value={warranty.warranty_type}
                      onValueChange={(v) => updateWarranty(i, 'warranty_type', v)}
                    >
                      <SelectTrigger className="w-40 h-8 dark:bg-slate-700 dark:border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MATERIAL">Material</SelectItem>
                        <SelectItem value="LABOR">Labor</SelectItem>
                        <SelectItem value="WORKMANSHIP">Workmanship</SelectItem>
                        <SelectItem value="PRODUCT">Product</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeWarranty(i)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs dark:text-slate-300">Warranty Name</Label>
                      <Input
                        value={warranty.name}
                        onChange={(e) => updateWarranty(i, 'name', e.target.value)}
                        placeholder="e.g., RhinoShield Coating Warranty"
                        className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs dark:text-slate-300">Term (Years)</Label>
                        <Input
                          type="number"
                          value={warranty.term_years}
                          onChange={(e) => updateWarranty(i, 'term_years', parseInt(e.target.value) || 1)}
                          className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                        />
                      </div>
                      <div className="flex items-end">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={warranty.is_transferable}
                            onCheckedChange={(v) => updateWarranty(i, 'is_transferable', v)}
                          />
                          <Label className="text-sm dark:text-slate-300">Transferable</Label>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs dark:text-slate-300">Description</Label>
                      <Textarea
                        value={warranty.description}
                        onChange={(e) => updateWarranty(i, 'description', e.target.value)}
                        placeholder="Brief description of warranty coverage"
                        rows={2}
                        className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs dark:text-slate-300">Coverage Details</Label>
                      <Textarea
                        value={warranty.coverage_details}
                        onChange={(e) => updateWarranty(i, 'coverage_details', e.target.value)}
                        placeholder="Detailed coverage information..."
                        rows={3}
                        className="mt-1 dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// SIGNATURE PAGE EDITOR
// ============================================================================

const SignaturePageEditor = ({ page, estimate, onUpdate, onRequestEmailSignature }) => {
  const signatures = estimate?.signatures || [];
  const hasPendingRequest = estimate?.pending_signature_request;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Signature Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">In-Person Signing</h4>
            <p className="text-sm text-blue-700 mb-3">
              Use Present Mode to have the customer sign directly on screen.
            </p>
            <Badge variant="outline" className="text-blue-700 border-blue-300">
              Available in Present Mode
            </Badge>
          </div>

          <div className="p-4 bg-amber-50 rounded-lg">
            <h4 className="font-medium text-amber-900 mb-2">Email Signature Request</h4>
            <p className="text-sm text-amber-700 mb-3">
              Send a signature request via email to the customer.
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onRequestEmailSignature}
              disabled={hasPendingRequest}
            >
              {hasPendingRequest ? (
                <>Pending Request Sent</>
              ) : (
                <>Request Signature via Email</>
              )}
            </Button>
            {hasPendingRequest && (
              <p className="text-xs text-amber-600 mt-2">
                A signature request is pending. The customer will receive an email link.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Existing Signatures */}
      {signatures.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Signatures Collected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {signatures.map((sig, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Check className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">{sig.signer_name}</p>
                    <p className="text-xs text-green-700">
                      Signed {new Date(sig.signed_at).toLocaleString()} via {sig.method}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================================================
// ABOUT COMPANY PAGE EDITOR (About Rhino Shield)
// ============================================================================

const AboutCompanyPageEditor = ({ page, onUpdate }) => {
  const config = page.config || {};
  
  const handleConfigChange = (key, value) => {
    onUpdate({ config: { ...config, [key]: value } });
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...(config.features || [])];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    handleConfigChange('features', newFeatures);
  };

  const addFeature = () => {
    const newFeatures = [...(config.features || []), { title: '', description: '' }];
    handleConfigChange('features', newFeatures);
  };

  const removeFeature = (index) => {
    const newFeatures = [...(config.features || [])];
    newFeatures.splice(index, 1);
    handleConfigChange('features', newFeatures);
  };

  return (
    <div className="space-y-6">
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-base dark:text-white">Company Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="dark:text-slate-300">Company Name</Label>
            <Input
              value={config.company_name || ''}
              onChange={(e) => handleConfigChange('company_name', e.target.value)}
              className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            />
          </div>
          <div>
            <Label className="dark:text-slate-300">Tagline</Label>
            <Input
              value={config.tagline || ''}
              onChange={(e) => handleConfigChange('tagline', e.target.value)}
              className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              placeholder="The Last Coat Your Home Will Ever Need"
            />
          </div>
          <div>
            <Label className="dark:text-slate-300">Introduction Text</Label>
            <Textarea
              value={config.intro_text || ''}
              onChange={(e) => handleConfigChange('intro_text', e.target.value)}
              className="mt-1 min-h-[100px] dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              placeholder="Describe your company and what makes you different..."
            />
          </div>
        </CardContent>
      </Card>

      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base dark:text-white">Key Features</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              checked={config.show_features !== false}
              onCheckedChange={(v) => handleConfigChange('show_features', v)}
            />
            <Label className="text-sm dark:text-slate-300">Show Features</Label>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(config.features || []).map((feature, index) => (
            <div key={index} className="p-4 border dark:border-slate-600 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <Input
                  value={feature.title || ''}
                  onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                  placeholder="Feature Title"
                  className="font-medium dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFeature(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                value={feature.description || ''}
                onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                placeholder="Feature description..."
                className="text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              />
            </div>
          ))}
          <Button variant="outline" onClick={addFeature} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Feature
          </Button>
        </CardContent>
      </Card>

      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-base dark:text-white">Custom Content</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={config.custom_content || ''}
            onChange={(e) => handleConfigChange('custom_content', e.target.value)}
            className="min-h-[150px] dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            placeholder="Add any additional custom content here..."
          />
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// WARRANTY PDF PAGE EDITOR (Detailed Warranty Document)
// ============================================================================

const WarrantyPDFPageEditor = ({ page, onUpdate }) => {
  const config = page.config || {};
  
  const handleConfigChange = (key, value) => {
    onUpdate({ config: { ...config, [key]: value } });
  };

  const handleListChange = (listKey, index, value) => {
    const newList = [...(config[listKey] || [])];
    newList[index] = value;
    handleConfigChange(listKey, newList);
  };

  const addListItem = (listKey) => {
    const newList = [...(config[listKey] || []), ''];
    handleConfigChange(listKey, newList);
  };

  const removeListItem = (listKey, index) => {
    const newList = [...(config[listKey] || [])];
    newList.splice(index, 1);
    handleConfigChange(listKey, newList);
  };

  return (
    <div className="space-y-6">
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-base dark:text-white">Warranty Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="dark:text-slate-300">Warranty Title</Label>
            <Input
              value={config.warranty_title || ''}
              onChange={(e) => handleConfigChange('warranty_title', e.target.value)}
              className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              placeholder="Rhino Shield 25-Year Transferable Warranty"
            />
          </div>
          <div>
            <Label className="dark:text-slate-300">Coverage Introduction</Label>
            <Textarea
              value={config.coverage_intro || ''}
              onChange={(e) => handleConfigChange('coverage_intro', e.target.value)}
              className="mt-1 min-h-[80px] dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              placeholder="This warranty covers..."
            />
          </div>
        </CardContent>
      </Card>

      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base dark:text-white">What&apos;s Covered</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              checked={config.show_covered_items !== false}
              onCheckedChange={(v) => handleConfigChange('show_covered_items', v)}
            />
            <Label className="text-sm dark:text-slate-300">Show</Label>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(config.covered_items || []).map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
              <Input
                value={item}
                onChange={(e) => handleListChange('covered_items', index, e.target.value)}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              />
              <Button variant="ghost" size="sm" onClick={() => removeListItem('covered_items', index)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addListItem('covered_items')}>
            <Plus className="h-4 w-4 mr-1" /> Add Item
          </Button>
        </CardContent>
      </Card>

      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base dark:text-white">Exclusions</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              checked={config.show_exclusions !== false}
              onCheckedChange={(v) => handleConfigChange('show_exclusions', v)}
            />
            <Label className="text-sm dark:text-slate-300">Show</Label>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(config.exclusions || []).map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <X className="h-4 w-4 text-red-500 flex-shrink-0" />
              <Input
                value={item}
                onChange={(e) => handleListChange('exclusions', index, e.target.value)}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              />
              <Button variant="ghost" size="sm" onClick={() => removeListItem('exclusions', index)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addListItem('exclusions')}>
            <Plus className="h-4 w-4 mr-1" /> Add Exclusion
          </Button>
        </CardContent>
      </Card>

      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base dark:text-white">Transfer Information</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              checked={config.show_transfer_info !== false}
              onCheckedChange={(v) => handleConfigChange('show_transfer_info', v)}
            />
            <Label className="text-sm dark:text-slate-300">Show</Label>
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            value={config.transfer_info || ''}
            onChange={(e) => handleConfigChange('transfer_info', e.target.value)}
            className="min-h-[80px] dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            placeholder="This warranty is fully transferable..."
          />
        </CardContent>
      </Card>

      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base dark:text-white">Claim Process</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              checked={config.show_claim_process !== false}
              onCheckedChange={(v) => handleConfigChange('show_claim_process', v)}
            />
            <Label className="text-sm dark:text-slate-300">Show</Label>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={config.claim_process || ''}
            onChange={(e) => handleConfigChange('claim_process', e.target.value)}
            className="min-h-[80px] dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            placeholder="To file a warranty claim..."
          />
          <div>
            <Label className="dark:text-slate-300">Contact Information</Label>
            <Input
              value={config.contact_info || ''}
              onChange={(e) => handleConfigChange('contact_info', e.target.value)}
              className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              placeholder="For warranty service, call..."
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// FINANCING PAGE EDITOR
// ============================================================================

const FinancingPageEditor = ({ page, estimate, onUpdate, onEstimateChange }) => {
  const config = page.config || {};
  const financingOptions = estimate?.financing_options || [];
  const showFinancing = estimate?.show_financing || false;
  const totalAmount = estimate?.total || estimate?.subtotal || 0;

  const calculateMonthlyPayment = (principal, apr, months) => {
    if (apr === 0) return principal / months;
    const monthlyRate = apr / 100 / 12;
    return (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
           (Math.pow(1 + monthlyRate, months) - 1);
  };

  const handleToggleFinancing = (enabled) => {
    onEstimateChange({ show_financing: enabled });
  };

  const addFinancingOption = () => {
    const newOptions = [...financingOptions, {
      id: `fin-${Date.now()}`,
      plan_name: 'New Plan',
      term_months: 12,
      apr_percent: 0,
      is_featured: false
    }];
    onEstimateChange({ financing_options: newOptions });
  };

  const updateFinancingOption = (index, field, value) => {
    const newOptions = [...financingOptions];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onEstimateChange({ financing_options: newOptions });
  };

  const removeFinancingOption = (index) => {
    const newOptions = [...financingOptions];
    newOptions.splice(index, 1);
    onEstimateChange({ financing_options: newOptions });
  };

  const loadDefaultOptions = async () => {
    try {
      const response = await axios.get(`${API}/settings/financing-defaults`);
      const defaults = response.data;
      const newOptions = (defaults.options || []).map((opt, i) => ({
        id: `fin-${Date.now()}-${i}`,
        plan_name: opt.plan_name,
        term_months: opt.term_months,
        apr_percent: opt.apr_percent,
        is_featured: i === 0
      }));
      onEstimateChange({ financing_options: newOptions });
      toast.success('Loaded default financing options');
    } catch (error) {
      toast.error('Failed to load default options');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base dark:text-white">Financing Options</CardTitle>
            <div className="flex items-center gap-2">
              <Switch
                checked={showFinancing}
                onCheckedChange={handleToggleFinancing}
              />
              <Label className="dark:text-slate-300">Show to Customer</Label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showFinancing && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Financing options are currently hidden from the customer.
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={loadDefaultOptions}>
              Load Default Options
            </Button>
            <Button variant="outline" onClick={addFinancingOption}>
              <Plus className="h-4 w-4 mr-2" />
              Add Custom Option
            </Button>
          </div>

          <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <strong>Estimate Total:</strong> ${totalAmount.toLocaleString()}
            </p>
          </div>

          {financingOptions.map((option, index) => {
            const monthlyPayment = calculateMonthlyPayment(
              totalAmount, option.apr_percent, option.term_months
            );
            
            return (
              <Card key={option.id || index} className="dark:bg-slate-700 dark:border-slate-600">
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Input
                      value={option.plan_name}
                      onChange={(e) => updateFinancingOption(index, 'plan_name', e.target.value)}
                      className="font-medium dark:bg-slate-600 dark:text-white"
                      placeholder="Plan Name"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFinancingOption(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs dark:text-slate-300">Term (Months)</Label>
                      <Input
                        type="number"
                        value={option.term_months}
                        onChange={(e) => updateFinancingOption(index, 'term_months', parseInt(e.target.value))}
                        className="dark:bg-slate-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs dark:text-slate-300">APR (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={option.apr_percent}
                        onChange={(e) => updateFinancingOption(index, 'apr_percent', parseFloat(e.target.value))}
                        className="dark:bg-slate-600 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded">
                    <span className="text-sm dark:text-green-300">Est. Monthly Payment:</span>
                    <span className="font-bold text-green-700 dark:text-green-300">
                      ${monthlyPayment.toFixed(2)}/mo
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={option.is_featured}
                      onCheckedChange={(v) => updateFinancingOption(index, 'is_featured', v)}
                    />
                    <Label className="text-sm dark:text-slate-300">Featured Option</Label>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {financingOptions.length === 0 && (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <p>No financing options configured.</p>
              <p className="text-sm">Click &quot;Load Default Options&quot; or &quot;Add Custom Option&quot; to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// TERMS PAGE EDITOR
// ============================================================================

const TermsPageEditor = ({ page, onUpdate }) => {
  // Derive directly from props
  const termsText = page.config?.terms_text || DEFAULT_TERMS_TEXT;

  const handleChange = (value) => {
    onUpdate({ config: { ...page.config, terms_text: value } });
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 text-amber-800">
          <Lock className="h-4 w-4" />
          <span className="text-sm font-medium">Terms page is always visible and locked to the end</span>
        </div>
      </div>

      <div>
        <Label>Terms & Conditions Text</Label>
        <Textarea
          value={termsText}
          onChange={(e) => handleChange(e.target.value)}
          className="mt-1 min-h-[400px] font-mono text-sm"
          placeholder="Enter terms and conditions..."
        />
      </div>

      <Button 
        variant="outline" 
        size="sm"
        onClick={() => handleChange(DEFAULT_TERMS_TEXT)}
      >
        Reset to Default
      </Button>
    </div>
  );
};

// ============================================================================
// MEDIA PAGE EDITOR (PDF, Image, Video)
// ============================================================================

const MediaPageEditor = ({ page, onUpdate, onDelete }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const pageType = page.page_type;
  const acceptTypes = {
    UPLOAD_PDF: '.pdf',
    UPLOAD_IMAGE: 'image/*',
    VIDEO: 'video/*'
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`${API}/upload`, formData);
      // Construct full URL from the returned path
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onUpdate({ 
        asset_url: fullUrl,
        title: page.title || file.name
      });
      toast.success('File uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Page Title</Label>
        <Input
          value={page.title || ''}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Page title"
          className="mt-1"
        />
      </div>

      <div>
        <Label>Upload {pageType === 'UPLOAD_PDF' ? 'PDF' : pageType === 'VIDEO' ? 'Video' : 'Image'}</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptTypes[pageType]}
          onChange={handleFileUpload}
          className="hidden"
        />
        
        {page.asset_url ? (
          <div className="mt-2">
            {pageType === 'UPLOAD_IMAGE' && (
              <img src={page.asset_url} alt="" className="max-h-48 rounded border" />
            )}
            {pageType === 'VIDEO' && (
              <video src={page.asset_url} controls className="max-h-48 rounded border" />
            )}
            {pageType === 'UPLOAD_PDF' && (
              <div className="p-4 bg-slate-100 rounded flex items-center gap-2">
                <FileText className="h-8 w-8 text-red-500" />
                <span className="text-sm">PDF uploaded</span>
              </div>
            )}
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                Replace
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onUpdate({ asset_url: null })}
                className="text-red-600"
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="mt-2"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Upload File
          </Button>
        )}
      </div>

      <div className="pt-4 border-t">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onDelete}
          className="text-red-600 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Page
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// ADD PAGE MODAL
// ============================================================================

const AddPageModal = ({ open, onClose, onAdd }) => {
  const [pageType, setPageType] = useState('UPLOAD_PDF');
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!title) {
        setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleAdd = async () => {
    setUploading(true);
    try {
      let assetUrl = null;
      
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await axios.post(`${API}/upload`, formData);
        // Construct full URL from the returned path
        assetUrl = `${process.env.REACT_APP_BACKEND_URL}${uploadRes.data.url}`;
      }

      onAdd({
        page_type: pageType,
        title: title || PAGE_TYPE_CONFIG[pageType]?.label || 'New Page',
        customer_visible: true,
        asset_url: assetUrl
      });

      // Reset
      setPageType('UPLOAD_PDF');
      setTitle('');
      setFile(null);
      onClose();
    } catch (error) {
      console.error('Error adding page:', error);
      toast.error('Failed to add page');
    } finally {
      setUploading(false);
    }
  };

  const acceptTypes = {
    UPLOAD_PDF: '.pdf',
    UPLOAD_IMAGE: 'image/*',
    VIDEO: 'video/*'
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Page</DialogTitle>
          <DialogDescription>
            Upload a PDF, image, or video to add to the estimate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label>Page Type</Label>
            <Select value={pageType} onValueChange={setPageType}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UPLOAD_PDF">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-red-500" />
                    PDF Document
                  </div>
                </SelectItem>
                <SelectItem value="UPLOAD_IMAGE">
                  <div className="flex items-center gap-2">
                    <Image className="h-4 w-4 text-cyan-500" />
                    Image
                  </div>
                </SelectItem>
                <SelectItem value="VIDEO">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-pink-500" />
                    Video
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Page Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter page title"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Upload File</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptTypes[pageType]}
              onChange={handleFileSelect}
              className="hidden"
            />
            {file ? (
              <div className="mt-2 p-3 bg-slate-100 rounded flex items-center justify-between">
                <span className="text-sm truncate">{file.name}</span>
                <Button variant="ghost" size="sm" onClick={() => setFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="mt-2 w-full"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Select File
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAdd} disabled={uploading}>
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Add Page
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MAIN ESTIMATE EDITOR COMPONENT
// ============================================================================

export const EstimateEditor = () => {
  const { jobId, estimateId } = useParams();
  const navigate = useNavigate();

  const [estimate, setEstimate] = useState(null);
  const [job, setJob] = useState(null);
  const [measurements, setMeasurements] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [jobPhotos, setJobPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [deletePageConfirm, setDeletePageConfirm] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [estRes, jobRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}/estimates/${estimateId}`),
        axios.get(`${API}/jobs/${jobId}`)
      ]);

      setEstimate(estRes.data);
      setJob(jobRes.data);

      // Try to fetch measurements
      try {
        const measRes = await axios.get(`${API}/jobs/${jobId}/measurements`);
        setMeasurements(measRes.data);
      } catch (e) {
        // Measurements may not exist
      }

      // Try to fetch line items
      try {
        const itemsRes = await axios.get(`${API}/jobs/${jobId}/estimates/${estimateId}/line-items`);
        setLineItems(itemsRes.data || []);
      } catch (e) {
        // Line items may not exist yet
        setLineItems([]);
      }

      // Try to fetch photos
      try {
        const photosRes = await axios.get(`${API}/jobs/${jobId}/photos`);
        setJobPhotos(photosRes.data || []);
      } catch (e) {
        // Photos may not exist
      }

      // Select first page by default
      if (estRes.data.pages?.length > 0) {
        setSelectedPage(estRes.data.pages[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load estimate');
    } finally {
      setLoading(false);
    }
  }, [jobId, estimateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sort pages (keeping Terms at end)
  const sortedPages = estimate?.pages
    ?.slice()
    .sort((a, b) => {
      // Terms always last
      if (a.page_type === 'TERMS') return 1;
      if (b.page_type === 'TERMS') return -1;
      return a.sort_order - b.sort_order;
    }) || [];

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      // Calculate pricing based on measurements and pricing mode
      const coatingSqft = estimate?.coating_sqft_override ?? measurements?.coating_sqft ?? 0;
      const repairSqft = estimate?.repair_sqft_override ?? measurements?.repair_sqft ?? 0;
      const totalSqft = coatingSqft + repairSqft;
      const pricePerSqft = (estimate?.pricing_mode || 'today') === 'today' ? 9.00 : 15.00;
      const baseCoatingPrice = totalSqft * pricePerSqft;
      
      // Calculate totals from line items
      const lineItemsSubtotal = lineItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
      const materialCostTotal = lineItems.reduce((sum, item) => 
        sum + (item.material_cost || 0) * (item.quantity || 1), 0);
      const laborCostTotal = lineItems.reduce((sum, item) => 
        sum + (item.labor_cost || 0) * (item.quantity || 1), 0);
      
      // Full subtotal = base coating price + line items
      const fullSubtotal = baseCoatingPrice + lineItemsSubtotal;

      // Update estimate with all fields including new Financial Control fields
      await axios.put(`${API}/jobs/${jobId}/estimates/${estimateId}`, {
        title: estimate.title,
        pricing_mode: estimate.pricing_mode,
        global_margin_percent: estimate.global_margin_percent,
        crew_labor_percent: estimate.crew_labor_percent,
        commission_percent: estimate.commission_percent,
        coating_sqft_override: estimate.coating_sqft_override,
        repair_sqft_override: estimate.repair_sqft_override,
        material_cost_total: materialCostTotal,
        labor_cost_total: laborCostTotal,
        subtotal: fullSubtotal,
        total: fullSubtotal,
        warranties: estimate.warranties,
        show_financing: estimate.show_financing,
        financing_options: estimate.financing_options,
        status: estimate.status
      });

      // Save pages
      for (const page of (estimate?.pages || [])) {
        await axios.put(
          `${API}/jobs/${jobId}/estimates/${estimateId}/pages/${page.id}`,
          {
            title: page.title,
            customer_visible: page.customer_visible,
            sort_order: page.sort_order,
            config: page.config,
            asset_url: page.asset_url
          }
        );
      }

      // Save line items
      for (const item of lineItems) {
        if (item.id?.startsWith('temp-')) {
          // Create new line item
          await axios.post(`${API}/jobs/${jobId}/estimates/${estimateId}/line-items`, item);
        } else {
          // Update existing
          await axios.put(`${API}/jobs/${jobId}/estimates/${estimateId}/line-items/${item.id}`, item);
        }
      }

      toast.success('Estimate saved');
      setHasUnsavedChanges(false);
      fetchData(); // Refresh to get server state
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  // Handle drag end for page reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    // Don't allow moving Terms page
    const draggedPage = sortedPages[sourceIndex];
    if (draggedPage.page_type === 'TERMS') return;

    // Don't allow dropping after Terms
    const termsIndex = sortedPages.findIndex(p => p.page_type === 'TERMS');
    if (destIndex >= termsIndex) return;

    const newPages = [...sortedPages];
    const [removed] = newPages.splice(sourceIndex, 1);
    newPages.splice(destIndex, 0, removed);

    // Update sort orders
    const updatedPages = newPages.map((p, i) => ({
      ...p,
      sort_order: i
    }));

    setEstimate(prev => ({ ...prev, pages: updatedPages }));
    setHasUnsavedChanges(true);
  };

  // Toggle page visibility
  const handleToggleVisibility = (page) => {
    if (page.page_type === 'TERMS') return; // Terms always visible

    const updatedPages = estimate.pages.map(p =>
      p.id === page.id ? { ...p, customer_visible: !p.customer_visible } : p
    );
    setEstimate(prev => ({ ...prev, pages: updatedPages }));
    setHasUnsavedChanges(true);

    // Update selected page if it's the one being toggled
    if (selectedPage?.id === page.id) {
      setSelectedPage({ ...page, customer_visible: !page.customer_visible });
    }
  };

  // Update page
  const handleUpdatePage = (updates) => {
    if (!selectedPage) return;

    const updatedPage = { ...selectedPage, ...updates };
    const updatedPages = estimate.pages.map(p =>
      p.id === selectedPage.id ? updatedPage : p
    );

    setEstimate(prev => ({ ...prev, pages: updatedPages }));
    setSelectedPage(updatedPage);
    setHasUnsavedChanges(true);
  };

  // Add new page
  const handleAddPage = async (pageData) => {
    try {
      const response = await axios.post(
        `${API}/jobs/${jobId}/estimates/${estimateId}/pages`,
        {
          ...pageData,
          sort_order: sortedPages.length - 1 // Before Terms
        }
      );

      toast.success('Page added');
      fetchData();
    } catch (error) {
      console.error('Error adding page:', error);
      toast.error('Failed to add page');
    }
  };

  // Delete page
  const handleDeletePage = async () => {
    if (!deletePageConfirm) return;

    try {
      await axios.delete(
        `${API}/jobs/${jobId}/estimates/${estimateId}/pages/${deletePageConfirm.id}`
      );

      toast.success('Page deleted');
      setDeletePageConfirm(null);

      if (selectedPage?.id === deletePageConfirm.id) {
        setSelectedPage(null);
      }

      fetchData();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page');
    }
  };

  // Handle line items change
  const handleLineItemsChange = (items) => {
    setLineItems(items);
    setHasUnsavedChanges(true);
  };

  // Handle estimate field changes (generic handler for financial controls)
  const handleEstimateChange = (updates) => {
    setEstimate(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  // Handle pricing mode change (today vs 90 day)
  const handlePricingModeChange = (mode) => {
    setEstimate(prev => ({ ...prev, pricing_mode: mode }));
    setHasUnsavedChanges(true);
  };

  // Handle warranties change
  const handleWarrantiesChange = (warranties) => {
    setEstimate(prev => ({ ...prev, warranties }));
    setHasUnsavedChanges(true);
  };

  // Request email signature (stub)
  const handleRequestEmailSignature = async () => {
    try {
      // For now, just mark as pending
      toast.success('Signature request created (email delivery not yet configured)');
      setEstimate(prev => ({ ...prev, pending_signature_request: true }));
    } catch (error) {
      toast.error('Failed to create signature request');
    }
  };

  // Navigate to present mode
  const handlePresent = () => {
    if (hasUnsavedChanges) {
      toast.warning('Please save your changes before presenting');
      return;
    }
    navigate(`/workspace/jobs/${jobId}/estimates/${estimateId}/present`);
  };

  // Navigate back
  const handleBack = () => {
    navigate(`/workspace/records?source=sales&mode=projects&id=${jobId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-4">Estimate not found</p>
          <Button onClick={handleBack}>Go Back</Button>
        </div>
      </div>
    );
  }

  // Render page editor based on type
  const renderPageEditor = () => {
    if (!selectedPage) {
      return (
        <div className="flex items-center justify-center h-full text-slate-500">
          Select a page to edit
        </div>
      );
    }

    switch (selectedPage.page_type) {
      case 'COVER':
        return (
          <CoverPageEditor
            page={selectedPage}
            job={job}
            jobPhotos={jobPhotos}
            onUpdate={handleUpdatePage}
          />
        );
      case 'DETAILS':
        return (
          <DetailsPageEditor
            page={selectedPage}
            estimate={estimate}
            measurements={measurements}
            lineItems={lineItems}
            onUpdate={handleUpdatePage}
            onLineItemsChange={handleLineItemsChange}
            onEstimateChange={handleEstimateChange}
            onPricingModeChange={handlePricingModeChange}
          />
        );
      case 'WARRANTY':
        return (
          <WarrantyPageEditor
            page={selectedPage}
            estimate={estimate}
            onUpdate={handleUpdatePage}
            onWarrantiesChange={handleWarrantiesChange}
          />
        );
      case 'SIGNATURE':
        return (
          <SignaturePageEditor
            page={selectedPage}
            estimate={estimate}
            onUpdate={handleUpdatePage}
            onRequestEmailSignature={handleRequestEmailSignature}
          />
        );
      case 'TERMS':
        return (
          <TermsPageEditor
            page={selectedPage}
            onUpdate={handleUpdatePage}
          />
        );
      case 'ABOUT_COMPANY':
        return (
          <AboutCompanyPageEditor
            page={selectedPage}
            onUpdate={handleUpdatePage}
          />
        );
      case 'WARRANTY_PDF':
        return (
          <WarrantyPDFPageEditor
            page={selectedPage}
            onUpdate={handleUpdatePage}
          />
        );
      case 'FINANCING':
        return (
          <FinancingPageEditor
            page={selectedPage}
            estimate={estimate}
            onUpdate={handleUpdatePage}
            onEstimateChange={handleEstimateChange}
          />
        );
      case 'UPLOAD_PDF':
      case 'UPLOAD_IMAGE':
      case 'VIDEO':
        return (
          <MediaPageEditor
            page={selectedPage}
            onUpdate={handleUpdatePage}
            onDelete={() => setDeletePageConfirm(selectedPage)}
          />
        );
      default:
        return (
          <div className="text-slate-500">
            Unknown page type: {selectedPage.page_type}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900" data-testid="estimate-editor">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div>
            <Input
              value={estimate.title}
              onChange={(e) => {
                setEstimate(prev => ({ ...prev, title: e.target.value }));
                setHasUnsavedChanges(true);
              }}
              className="font-semibold text-lg border-none p-0 h-auto focus-visible:ring-0 bg-transparent dark:text-white"
              placeholder="Estimate Title"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">{estimate.estimate_number}</p>
          </div>

          {hasUnsavedChanges && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-500">
              Unsaved changes
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePresent}>
            <Presentation className="h-4 w-4 mr-2" />
            Present
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Three-Panel Layout */}
      <div className="flex h-[calc(100vh-57px)]">
        {/* Left Panel - Page List */}
        <div className="w-64 bg-white dark:bg-slate-800 border-r dark:border-slate-700 flex flex-col">
          <div className="p-3 border-b dark:border-slate-700">
            <h3 className="font-medium text-sm text-slate-700 dark:text-slate-200">Pages</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Drag to reorder. Toggle visibility.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="pages">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-2"
                  >
                    {sortedPages.map((page, index) => (
                      <PageListItem
                        key={page.id}
                        page={page}
                        index={index}
                        isSelected={selectedPage?.id === page.id}
                        onSelect={setSelectedPage}
                        onToggleVisibility={handleToggleVisibility}
                        isTerms={page.page_type === 'TERMS'}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          {/* Add Page Button */}
          <div className="p-2 border-t dark:border-slate-700">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowAddPageModal(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Page
            </Button>
          </div>
        </div>

        {/* Center Panel - Page Editor */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-100 dark:bg-slate-900">
          <Card className="max-w-2xl mx-auto dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg dark:text-white">
                  {selectedPage ? (PAGE_TYPE_CONFIG[selectedPage.page_type]?.label || 'Page') : 'Page Editor'}
                </CardTitle>
                {selectedPage && selectedPage.page_type !== 'TERMS' && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-500 dark:text-slate-400">Customer Visible</Label>
                    <Switch
                      checked={selectedPage.customer_visible}
                      onCheckedChange={() => handleToggleVisibility(selectedPage)}
                    />
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {renderPageEditor()}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Preview / Summary */}
        <div className="w-72 bg-white dark:bg-slate-800 border-l dark:border-slate-700 p-4 overflow-y-auto">
          <h3 className="font-medium text-sm text-slate-700 dark:text-slate-200 mb-4">Estimate Summary</h3>

          <div className="space-y-4">
            {/* Status */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Status</p>
              <Badge>{estimate.status}</Badge>
            </div>

            {/* Visible Pages */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Customer-Visible Pages</p>
              <p className="font-medium dark:text-white">
                {sortedPages.filter(p => p.customer_visible).length} of {sortedPages.length}
              </p>
            </div>

            {/* Pricing - Calculate dynamically */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Pricing</p>
              {(() => {
                const coatingSqft = estimate?.coating_sqft_override ?? measurements?.coating_sqft ?? 0;
                const repairSqft = estimate?.repair_sqft_override ?? measurements?.repair_sqft ?? 0;
                const totalSqft = coatingSqft + repairSqft;
                const pricePerSqft = (estimate?.pricing_mode || 'today') === 'today' ? 9.00 : 15.00;
                const basePrice = totalSqft * pricePerSqft;
                const lineItemsTotal = lineItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
                const calculatedSubtotal = basePrice + lineItemsTotal;
                
                return (
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="dark:text-slate-300">Coating ({totalSqft.toLocaleString()} sqft)</span>
                      <span className="font-medium dark:text-white">${basePrice.toLocaleString()}</span>
                    </div>
                    {lineItemsTotal > 0 && (
                      <div className="flex justify-between">
                        <span className="dark:text-slate-300">Line Items</span>
                        <span className="font-medium dark:text-white">${lineItemsTotal.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t dark:border-slate-600 pt-1 font-medium">
                      <span className="dark:text-slate-300">Total</span>
                      <span className="text-lg dark:text-white">${calculatedSubtotal.toLocaleString()}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Quick Actions */}
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handlePresent}
              >
                <Presentation className="h-4 w-4 mr-2" />
                Present to Customer
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  // Will implement send flow
                  toast.info('Save the estimate first, then use Send from the Estimates tab');
                }}
              >
                <Send className="h-4 w-4 mr-2" />
                Send to Customer
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Page Modal */}
      <AddPageModal
        open={showAddPageModal}
        onClose={() => setShowAddPageModal(false)}
        onAdd={handleAddPage}
      />

      {/* Delete Page Confirmation */}
      <AlertDialog open={!!deletePageConfirm} onOpenChange={() => setDeletePageConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Page?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this page? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePage} className="bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EstimateEditor;
